import React from 'react';
import classNames from 'classnames';
import {
  ControlledChatRoomScreen,
  ChatWidget,
  expandCollapseManager,
  AvailabilityStatus,
  MessageNotification,
  ThemeProvider,
} from '@wix/chat-web';
import wix from '../../../services/wix';
import serverApi from '../../../services/serverApi';
import {
  updateEditorSettingsDesign,
  updateEditorSettingsLayout,
  updateEditorSettingsTexts,
  updateSiteColors,
  updateStyleParamsColors,
  updateVisibilitySettings,
  updateBusinessChatEnabled,
  updateSocialChatEnabled,
  updateBusinessChatRoomId,
  updateShowSocialListInEditor,
  collapseWidget,
  expandWidget,
  updateStyleParamsFonts,
  updateIsBranded,
  updateEditorSettingsBehaviour,
} from '../../../actions';
import { connect } from 'react-redux';
import {
  editorSettingsAvatar,
  getLocalizedTexts,
  isExperimentOn,
  theme,
} from '../../../reducers';
import _get from 'lodash/get';
import {
  EDITOR_EVENT_TYPE,
  EDITOR_TPA_TYPE,
} from '../../../constants/editor-event-types';
import { Message } from '@wix/chat-sdk';
import { ON_NEW_MESSAGE } from '@wix/inbox-common';
import { AVATARS } from '../../../constants/avatars';
import { ControlledSocialChatList } from '../../SocialChatList/SocialChatList';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { fakeBusinessChatroomId } from '../../../constants/editor-fake-display-data';
import Branding from '../../Branding/Branding';
import * as biLogger from '../../../services/biLogger';
import { isPremium } from '../../../services/premium';
import { fedopsLogger } from '../../../services/fedopsLogger';
import Avatar from '../../Avatar/Avatar';
import s from './EditorWidget.scss';
import * as Sentry from '@sentry/browser';

export interface EditorWidgetProps extends InjectedTranslateProps {
  widgetExpanded: boolean;
  chatInitResult: any;
  visibilitySettings: any;
  editorSettingsLayout: any;
  localizedTexts: any;
  editorSettingsAvatar: any;
  editorSettingsBehaviour: any;
  theme: any;
  businessChatEnabled: boolean;
  socialChatEnabled: boolean;
  showSocialListInEditor: boolean;
  isBranded: boolean;
  locale: string;
  visibilitySettingsChange: Function;
  businessChatEnabledChange: Function;
  socialChatEnabledChange: Function;
  showSocialListInEditorChange: Function;
  siteColorsChange: Function;
  styleParamsColorsChange: Function;
  styleParamsFontsChange: Function;
  businessChatroomIdChange: Function;
  updateIsBranded: Function;
  expandWidget: Function;
  editorSettingsTextsChange: Function;
  editorSettingsLayoutChange: Function;
  editorSettingsDesignChange: Function;
  editorSettingsBehaviourChange: Function;
  collapseWidget: Function;
}

export interface EditorWidgetState {
  messages: any[];
  rooms: any[];
  availabilityStatus: AvailabilityStatus | undefined;
}

class EditorWidget extends React.Component<
  EditorWidgetProps,
  EditorWidgetState
> {
  readonly state = {
    messages: [],
    rooms: [],
    availabilityStatus: AvailabilityStatus.Available,
  };

  componentDidMount() {
    Sentry.addBreadcrumb({ category: 'MountedWidget', message: 'Editor' });
    this.showOrHideChat();
    wix.subscribeForPublicDataChanged(this.onPublicDataChanged);
    expandCollapseManager.registerOnExpand(this.handleExpand);
    expandCollapseManager.registerOnCollapse(this.collapseHandler);
    wix.subscribeForSettingsUpdated(
      ({
        collapse,
        chatVisible,
        messages,
        availabilityStatus,
        socialChatEnabled,
        businessChatEnabled,
        showSocialList,
        rooms,
      }) => {
        if (collapse !== undefined) {
          collapse ? this.handleCollapse() : this.handleExpand();
        }
        if (chatVisible !== undefined) {
          chatVisible
            ? wix.resizeToCollapsedMode(this.isCollapsedAsRounded())
            : this.onHide();
          this.props.visibilitySettingsChange({
            editorVisibility: chatVisible,
          });
        }
        if (messages) {
          const newMessages = messages.map((m) => new Message(m));
          this.setState({ messages: newMessages });
        }
        if (rooms !== undefined) {
          this.setState({ rooms });
        }
        if (availabilityStatus !== undefined) {
          this.setState({ availabilityStatus });
        }
        if (socialChatEnabled !== undefined) {
          this.props.socialChatEnabledChange(socialChatEnabled);
        }
        if (businessChatEnabled !== undefined) {
          this.props.businessChatEnabledChange(businessChatEnabled);
        }
        if (showSocialList !== undefined) {
          this.props.showSocialListInEditorChange(showSocialList);
        }
      },
    );

    if (this.isAfterInstall()) {
      void serverApi.setEditorVisibilityOn();
    }
    wix.subscribeForThemeChanged(({ siteColors, style: { colors, fonts } }) => {
      this.props.styleParamsColorsChange(colors);
      this.props.styleParamsFontsChange(fonts);
      this.props.siteColorsChange(siteColors);
    });
    wix.subscribeForSettingsClosed(({ type, tpaType }) => {
      type === EDITOR_EVENT_TYPE.SETTINGS_PANEL_CLOSED &&
        tpaType === EDITOR_TPA_TYPE &&
        this.props.widgetExpanded &&
        this.handleCollapse();
    });
    this.props.businessChatroomIdChange(fakeBusinessChatroomId);

    const isBranded = this.props.chatInitResult.isBranded;
    if (isBranded !== undefined) {
      const isChatPremium = !isBranded;

      if (isChatPremium) {
        this.props.updateIsBranded(isBranded);
      } else {
        isPremium()
          .then((_isPremium) => {
            const isFreePackage = !_isPremium;
            this.props.updateIsBranded(isFreePackage);
          })
          .catch(() => {
            /* do nothing */
          });
      }
    }
  }

  componentWillUnmount() {
    expandCollapseManager.unregisterOnExpand(this.handleExpand);
    expandCollapseManager.unregisterOnCollapse(this.handleCollapse);
  }

  shouldShowChat = () => this.props.visibilitySettings.editorVisibility;

  isAfterInstall = () =>
    this.props.visibilitySettings.editorVisibility &&
    !this.props.visibilitySettings.chatOnboarded;

  showOrHideChat() {
    this.shouldShowChat() ? this.handleCollapse() : this.onHide();
  }

  onPublicDataChanged = ({ design, layout, texts, behaviour }) => {
    design && this.props.editorSettingsDesignChange(design);
    layout && this.props.editorSettingsLayoutChange(layout);
    texts && this.props.editorSettingsTextsChange(texts);
    behaviour && this.props.editorSettingsBehaviourChange(behaviour);

    if (layout) {
      void wix.resizeToCollapsedMode(this.isCollapsedAsRounded());
    }

    if (behaviour) {
      if (behaviour.onNewMessage === ON_NEW_MESSAGE.SHOW_NOTIFICATION) {
        this.handleCollapse(true);
      } else {
        this.handleExpand();
      }
    }
  };

  handleExpand = () => {
    this.props.expandWidget();
  };

  collapseHandler = (data) => {
    this.handleCollapse();
  };

  handleCollapse = (withMessageNotification = false) => {
    this.props.collapseWidget(withMessageNotification);
  };

  onHide = () => wix.resizeToHidden();

  isCollapsedAsRounded = () => {
    return _get(this.props.editorSettingsLayout, 'collapsedAsRounded', false);
  };

  onChatDisplayed = () => {
    if (this.shouldShowChat()) {
      const asRounded = _get(
        this.props.editorSettingsLayout,
        'collapsedAsRounded',
        false,
      );
      const layoutName = asRounded
        ? 'collapsedAsRounded'
        : 'selectedRadiusOption';
      biLogger.chatDisplayedEvent(wix.getDeviceType(), layoutName);
      fedopsLogger.appLoaded();
    }
  };

  eventHooks = {
    onChatDisplayed: this.onChatDisplayed,
  };

  getTitle = (isSocialChatList, isMemberChatRoom, isAvailable) => {
    const {
      localizedTexts: { onlineTitle, offlineTitle },
      t,
    } = this.props;

    const { rooms } = this.state;
    const memberChatName = rooms.length > 0 ? (rooms[0] as any).name : '';

    const isMemberChatRoomWithNoMemberName =
      isMemberChatRoom && !memberChatName;
    return isSocialChatList || isMemberChatRoomWithNoMemberName
      ? t('social.chatroom-list.title')
      : isMemberChatRoom
      ? memberChatName
      : isAvailable
      ? onlineTitle
      : offlineTitle;
  };

  isMessageNotificationEnabled = () => {
    const {
      editorSettingsBehaviour: { onNewMessage },
    } = this.props;
    const { messages } = this.state;

    return (
      messages.length > 0 && onNewMessage === ON_NEW_MESSAGE.SHOW_NOTIFICATION
    );
  };

  render() {
    const { messages, rooms, availabilityStatus } = this.state;
    const {
      localizedTexts: { onlineMessage, offlineMessage },
      editorSettingsBehaviour: { responseTimeEnabled, responseTimeSelected },
      businessChatEnabled,
      socialChatEnabled,
      showSocialListInEditor,
      widgetExpanded,
    } = this.props;
    const avatar = this.props.editorSettingsAvatar;
    const isAvailable = availabilityStatus !== AvailabilityStatus.Away;
    const isSocialChatList = socialChatEnabled && showSocialListInEditor;
    const isMemberChatRoom = socialChatEnabled && !showSocialListInEditor;
    const title = this.getTitle(
      isSocialChatList,
      isMemberChatRoom,
      isAvailable,
    );
    const typicallyReplies = businessChatEnabled
      ? {
          isEnabled: responseTimeEnabled,
          period: responseTimeSelected,
        }
      : undefined;
    const controlledSocialChatListProps: any = {
      chatrooms: rooms,
      includeBusinessChatroom: businessChatEnabled,
    };

    const isMobile = wix.getDeviceType() === 'mobile';

    return this.shouldShowChat() ? (
      <div>
        {this.isMessageNotificationEnabled() && !widgetExpanded && (
          <div
            className={classNames(s.messageNotification, {
              [s.asRounded]: this.isCollapsedAsRounded(),
              [s.isExpanded]: widgetExpanded,
            })}
          >
            <ThemeProvider theme={this.props.theme}>
              <MessageNotification
                messages={messages}
                onClick={() => {}}
                onClose={() => {}}
                avatar={<Avatar size={30} avatar={avatar} />}
                title={title}
                withCallToAction={true}
                locale={this.props.locale}
              />
            </ThemeProvider>
          </div>
        )}
        <ChatWidget
          isExpanded={widgetExpanded}
          onlineTitle={title}
          onlineMessage={
            isMemberChatRoom ? '' : isAvailable ? onlineMessage : offlineMessage
          }
          avatar={socialChatEnabled ? AVATARS.NONE.prop : avatar}
          theme={this.props.theme}
          locale={this.props.locale}
          availabilityStatus={availabilityStatus}
          unfoldable={!isSocialChatList}
          eventHooks={this.eventHooks}
          isMobile={isMobile}
          typicallyReplies={!isSocialChatList ? typicallyReplies : undefined}
        >
          {isSocialChatList ? (
            <ControlledSocialChatList {...controlledSocialChatListProps} />
          ) : (
            <ControlledChatRoomScreen
              banner={this.props.isBranded ? <Branding /> : undefined}
              messages={messages}
              locale={this.props.locale}
              theme={this.props.theme}
            />
          )}
        </ChatWidget>
      </div>
    ) : null;
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    styleParamsColorsChange: (colors) =>
      dispatch(updateStyleParamsColors(colors)),
    styleParamsFontsChange: (fonts) => dispatch(updateStyleParamsFonts(fonts)),
    siteColorsChange: (color) => dispatch(updateSiteColors(color)),
    editorSettingsDesignChange: (design) =>
      dispatch(updateEditorSettingsDesign(design)),
    editorSettingsLayoutChange: (layout) =>
      dispatch(updateEditorSettingsLayout(layout)),
    editorSettingsTextsChange: (texts) =>
      dispatch(updateEditorSettingsTexts(texts)),
    editorSettingsBehaviourChange: (behaviour) =>
      dispatch(updateEditorSettingsBehaviour(behaviour)),
    visibilitySettingsChange: (texts) =>
      dispatch(updateVisibilitySettings(texts)),
    businessChatEnabledChange: (enabled) =>
      dispatch(updateBusinessChatEnabled(enabled)),
    socialChatEnabledChange: (enabled) =>
      dispatch(updateSocialChatEnabled(enabled)),
    businessChatroomIdChange: (businessChatroomId) =>
      dispatch(updateBusinessChatRoomId(businessChatroomId)),
    showSocialListInEditorChange: (shouldShow) =>
      dispatch(updateShowSocialListInEditor(shouldShow)),
    expandWidget: () => dispatch(expandWidget()),
    collapseWidget: (withMessageNotification = false) =>
      dispatch(collapseWidget(withMessageNotification)),
    updateIsBranded: (isBranded) => dispatch(updateIsBranded(isBranded)),
  };
};

function mapStateToProps(state) {
  return {
    theme: theme(state),
    editorSettingsAvatar: editorSettingsAvatar(state),
    localizedTexts: getLocalizedTexts(state),
    editorSettingsLayout: state.editorSettingsLayout,
    editorSettingsBehaviour: state.editorSettingsBehaviour,
    visibilitySettings: state.visibilitySettings,
    locale: state.locale,
    chatInitResult: state.chatInitResult,
    isBranded: state.isBranded,
    socialChatEnabled: state.socialChatEnabled,
    businessChatEnabled: state.businessChatEnabled,
    showSocialListInEditor: state.showSocialListInEditor,
    widgetExpanded: state.widgetExpanded,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(translate()(EditorWidget));
